import { createSlice } from '@reduxjs/toolkit';
import {
  HandlersActionResponse,
  HandlersLibraryTreeNode,
} from '../athenaApiGen';

type TModalState = {
  uploader: {
    open: boolean;
  };
  deleteConfirmation: {
    open: boolean;
  };
  movePicker: {
    open: boolean;
  };
};

type TUploadPayload = {
  objectId: string;
  objectType: string;
  formSectionId?: string;
  name: string;
  description?: string;
  creatorContactId: string;
};

type TLibraryInitialState = {
  selectedItem: HandlersLibraryTreeNode | null;
  uploadPayload: TUploadPayload;
  isItemLoading: boolean;
  allowedActions: HandlersActionResponse[];
  infoBar: {
    isOpen: boolean;
    details: string;
  };
  modals: TModalState;
  // Tree-item id queued to auto-enter rename mode the next time it mounts.
  // Set by the "Add Folder" kebab action so the newly-created Untitled folder
  // becomes immediately editable. Cleared by the CustomTreeItem effect once it
  // has triggered, so re-mounts of the same id don't re-enter edit mode.
  pendingEditItemId: string | null;
  // Tree-item id that was just created via the inline "New Folder" flow and
  // is awaiting its first real rename. Used by CustomTreeItem to roll the
  // create back when the user cancels (Escape / X) — the folder is created
  // optimistically before the rename input opens, so cancel needs to delete
  // it; otherwise Escape would leave an orphan "Untitled" folder behind
  // (issue #1327). Cleared on successful save in DynamicTree.onItemLabelChange.
  pendingCreateItemId: string | null;
  // Tree-node id of the item the user wants to move. Set when "Move to..."
  // is clicked on a kebab; the MovePickerModal reads this to know which
  // item the picker is selecting a destination for. Independent of
  // selectedItem so the right pane doesn't have to track the move state.
  moveTargetId: string | null;
};

const initialState: TLibraryInitialState = {
  selectedItem: null,
  isItemLoading: false,
  allowedActions: [],
  infoBar: {
    isOpen: false,
    details: '',
  },
  uploadPayload: {
    objectId: '',
    objectType: '',
    name: '',
    creatorContactId: '',
  },
  pendingEditItemId: null,
  pendingCreateItemId: null,
  moveTargetId: null,
  modals: {
    uploader: {
      open: false,
    },
    deleteConfirmation: {
      open: false,
    },
    movePicker: {
      open: false,
    },
  },
};

const librarySlice = createSlice({
  name: 'library',
  initialState,
  reducers: {
    setSelectedItem: (state, action) => {
      state.selectedItem = action.payload;
    },
    setIsItemLoading: (state, action) => {
      state.isItemLoading = action.payload;
    },
    updateUploadPayload: (state, action) => {
      state.uploadPayload = {
        ...state.uploadPayload,
        ...action.payload,
      };
    },
    setInfoBarDetail: (state, action) => {
      state.infoBar = {
        isOpen: true,
        details: action.payload,
      };
    },
    closeInfoBar: (state) => {
      state.infoBar = {
        isOpen: false,
        details: '',
      };
    },
    setAllowedActions: (state, action) => {
      state.allowedActions = action.payload;
    },
    clearUploadPayload: (state) => {
      state.uploadPayload = initialState.uploadPayload;
    },
    setPendingEditItemId: (state, action) => {
      state.pendingEditItemId = action.payload;
    },
    setPendingCreateItemId: (state, action) => {
      state.pendingCreateItemId = action.payload;
    },
    setMoveTargetId: (state, action) => {
      state.moveTargetId = action.payload;
    },
    toggleModal: (state, action) => {
      state.modals = {
        ...state.modals,
        [action.payload]: {
          open: !state.modals[
            action.payload as
              | 'uploader'
              | 'deleteConfirmation'
              | 'movePicker'
          ].open,
        },
      };
    },
  },
});
const {
  setSelectedItem,
  setInfoBarDetail,
  closeInfoBar,
  toggleModal,
  updateUploadPayload,
  clearUploadPayload,
  setIsItemLoading,
  setAllowedActions,
  setPendingEditItemId,
  setPendingCreateItemId,
  setMoveTargetId,
} = librarySlice.actions;

export {
  clearUploadPayload,
  closeInfoBar,
  setAllowedActions,
  setInfoBarDetail,
  setIsItemLoading,
  setMoveTargetId,
  setPendingCreateItemId,
  setPendingEditItemId,
  setSelectedItem,
  toggleModal,
  updateUploadPayload,
};

export default librarySlice.reducer;
